/**
 * Pointer-Events-based swipe-to-action hook (shared — @bbpos/ui).
 *
 * Single Pointer Events handler covers touch, mouse and pen with one API
 * (no parallel onTouchStart/onMouseDown paths). Designed for the
 * "swipe-to-mark-read" gesture on notification cards but reusable for any
 * single-direction swipe-to-commit affordance on any list row.
 *
 * Lifecycle:
 *
 *   idle ──pointerdown──> dragging ──release past threshold──> committed
 *     ▲                       │                                    │
 *     │                       │ release below threshold            │
 *     │                       ▼                                    ▼
 *     └────────── settling ◀──┴──────────── after onCommit ────────┘
 *
 * - axis lock: on the first move, if the dominant axis is vertical (within
 *   ~10° tolerance) the gesture is abandoned so the parent can keep
 *   scrolling. The pointer is released and `state` returns to 'idle'.
 * - velocity-aware commit: a flick faster than 0.5 px/ms in the active
 *   direction commits even if the position threshold was not reached.
 * - prefers-reduced-motion: snap-back / dismiss are instantaneous (no
 *   transition string emitted in the bound `style`).
 * - cleanup: pointerup, pointercancel and unmount all release pointer
 *   capture defensively.
 */

import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type CSSProperties,
  type PointerEvent as ReactPointerEvent,
  type ReactNode,
  type RefCallback,
} from 'react';

// Local tiny class-joiner so @bbpos/ui stays app-independent.
function cn(...classes: Array<string | false | null | undefined>): string {
  return classes.filter(Boolean).join(' ');
}

// ─── Public types ───────────────────────────────────────────────────────────

export type SwipeState = 'idle' | 'dragging' | 'committed' | 'settling';
export type SwipeDirection = 'left' | 'right';

export interface UseSwipeToActionOptions {
  /** Called once when a gesture commits (past threshold or fast flick). */
  onCommit: () => void;
  /**
   * Distance in pixels at which a release commits the gesture. Defaults to
   * `min(88, 0.35 * cardWidth)` measured at pointerdown time.
   */
  threshold?: number;
  /** Which direction to detect. Default `'left'`. */
  direction?: SwipeDirection;
  /** When true, all pointer handlers no-op. */
  disabled?: boolean;
  /**
   * If true, the dismiss animation off-screen runs after commit. The hook
   * does not unmount the element — the consumer is expected to flip its
   * own state in `onCommit` and re-render. Default true.
   */
  dismissAfterCommit?: boolean;
}

export interface SwipeBindProps {
  ref: RefCallback<HTMLElement>;
  style: CSSProperties;
  onPointerDown: (e: ReactPointerEvent<HTMLElement>) => void;
  onPointerMove: (e: ReactPointerEvent<HTMLElement>) => void;
  onPointerUp: (e: ReactPointerEvent<HTMLElement>) => void;
  onPointerCancel: (e: ReactPointerEvent<HTMLElement>) => void;
}

export interface UseSwipeToActionResult {
  bind: SwipeBindProps;
  state: SwipeState;
  /** 0..1 — distance dragged divided by the resolved threshold. */
  progress: number;
  /** Total |dx| in px since pointerdown. Useful for "tap vs drag" decisions. */
  totalDelta: number;
}

// ─── Constants ──────────────────────────────────────────────────────────────

const DEFAULT_THRESHOLD_PX = 88;
const RELATIVE_THRESHOLD = 0.35;
const FLICK_VELOCITY_PX_PER_MS = 0.5;
const AXIS_LOCK_TOLERANCE_RATIO = Math.tan((10 * Math.PI) / 180); // ≈0.176
const SETTLE_TRANSITION = 'transform 220ms cubic-bezier(.2,.9,.3,1)';
const DISMISS_TRANSITION = 'transform 200ms cubic-bezier(.2,.7,.4,1)';
const DISMISS_OFFSET_PX = 480;

// ─── Hook ───────────────────────────────────────────────────────────────────

export function useSwipeToAction(options: UseSwipeToActionOptions): UseSwipeToActionResult {
  const {
    onCommit,
    threshold,
    direction = 'left',
    disabled = false,
    dismissAfterCommit = true,
  } = options;

  const [state, setState] = useState<SwipeState>('idle');
  const [offset, setOffset] = useState(0);
  const [totalDelta, setTotalDelta] = useState(0);

  // Refs survive re-renders without re-binding listeners.
  const elementRef = useRef<HTMLElement | null>(null);
  const activePointerIdRef = useRef<number | null>(null);
  const startXRef = useRef(0);
  const startYRef = useRef(0);
  const startTimeRef = useRef(0);
  const axisLockedRef = useRef(false);
  const resolvedThresholdRef = useRef(DEFAULT_THRESHOLD_PX);
  const onCommitRef = useRef(onCommit);
  onCommitRef.current = onCommit;

  // Latest reduced-motion preference. Read once per gesture to avoid the
  // cost of evaluating matchMedia inside the move handler.
  const reducedMotion =
    typeof window !== 'undefined' && typeof window.matchMedia === 'function'
      ? window.matchMedia('(prefers-reduced-motion: reduce)').matches
      : false;

  // ─── Reset on disable change / unmount ────────────────────────────────
  useEffect(() => {
    if (disabled) {
      setState('idle');
      setOffset(0);
      setTotalDelta(0);
      activePointerIdRef.current = null;
    }
  }, [disabled]);

  useEffect(() => {
    return () => {
      // Defensive: release any pointer capture on unmount.
      const el = elementRef.current;
      const id = activePointerIdRef.current;
      if (el && id !== null && typeof el.releasePointerCapture === 'function') {
        try {
          el.releasePointerCapture(id);
        } catch {
          /* already released or invalid id */
        }
      }
    };
  }, []);

  // ─── Helpers ──────────────────────────────────────────────────────────

  const resolveThreshold = useCallback(
    (el: HTMLElement): number => {
      if (typeof threshold === 'number') return threshold;
      const width = el.getBoundingClientRect().width;
      // jsdom returns 0 for getBoundingClientRect — fall back to default.
      if (!width) return DEFAULT_THRESHOLD_PX;
      return Math.min(DEFAULT_THRESHOLD_PX, width * RELATIVE_THRESHOLD);
    },
    [threshold],
  );

  const releaseCapture = useCallback((el: HTMLElement | null, id: number | null) => {
    if (!el || id === null) return;
    if (typeof el.releasePointerCapture === 'function') {
      try {
        el.releasePointerCapture(id);
      } catch {
        /* ignore */
      }
    }
  }, []);

  // ─── Pointer handlers ─────────────────────────────────────────────────

  const onPointerDown = useCallback(
    (e: ReactPointerEvent<HTMLElement>) => {
      if (disabled) return;
      // Ignore secondary pointers.
      if (activePointerIdRef.current !== null) return;
      // Right click / middle click: ignore.
      if (e.pointerType === 'mouse' && e.button !== 0) return;

      const target = e.currentTarget;
      activePointerIdRef.current = e.pointerId;
      startXRef.current = e.clientX;
      startYRef.current = e.clientY;
      startTimeRef.current = e.timeStamp || Date.now();
      axisLockedRef.current = false;
      resolvedThresholdRef.current = resolveThreshold(target);

      // Pointer capture waits for the horizontal axis lock in onPointerMove:
      // capturing here would retarget a plain tap's click to this element,
      // so a nested <button onClick> would never run (PS2-004).

      setState('dragging');
      setOffset(0);
      setTotalDelta(0);
    },
    [disabled, resolveThreshold],
  );

  const onPointerMove = useCallback(
    (e: ReactPointerEvent<HTMLElement>) => {
      if (disabled) return;
      if (activePointerIdRef.current !== e.pointerId) return;

      const dx = e.clientX - startXRef.current;
      const dy = e.clientY - startYRef.current;
      const absDx = Math.abs(dx);
      const absDy = Math.abs(dy);

      // First meaningful move: decide whether to lock the axis as horizontal
      // or abandon the gesture entirely (vertical scroll wins).
      if (!axisLockedRef.current && absDx + absDy > 4) {
        const isVerticalDominant = absDy > absDx && absDy * AXIS_LOCK_TOLERANCE_RATIO > absDx;
        if (isVerticalDominant) {
          // Abandon — return to idle and release the pointer so the parent
          // scroll container can take over.
          releaseCapture(elementRef.current, activePointerIdRef.current);
          activePointerIdRef.current = null;
          setState('idle');
          setOffset(0);
          setTotalDelta(0);
          return;
        }
        axisLockedRef.current = true;
        const target = e.currentTarget;
        if (typeof target.setPointerCapture === 'function') {
          try {
            target.setPointerCapture(e.pointerId);
          } catch {
            /* jsdom or unsupported environment — gesture still works */
          }
        }
      }

      if (!axisLockedRef.current) return;

      // Direction filter: only count motion along the active direction.
      // For 'left', dx is negative; for 'right', dx is positive.
      const directional = direction === 'left' ? Math.min(0, dx) : Math.max(0, dx);
      setOffset(directional);
      setTotalDelta(absDx);
    },
    [disabled, direction, releaseCapture],
  );

  const onPointerUp = useCallback(
    (e: ReactPointerEvent<HTMLElement>) => {
      if (disabled) return;
      if (activePointerIdRef.current !== e.pointerId) return;

      const el = elementRef.current;
      releaseCapture(el, e.pointerId);
      activePointerIdRef.current = null;

      // Tap or abandoned drag — nothing to commit.
      if (!axisLockedRef.current) {
        setState('idle');
        setOffset(0);
        return;
      }

      const dx = e.clientX - startXRef.current;
      const directional = direction === 'left' ? -Math.min(0, dx) : Math.max(0, dx);
      const elapsed = (e.timeStamp || Date.now()) - startTimeRef.current;
      const velocity = elapsed > 0 ? directional / elapsed : 0;

      const pastThreshold = directional >= resolvedThresholdRef.current;
      const fastFlick = velocity >= FLICK_VELOCITY_PX_PER_MS && directional > 8;

      if (pastThreshold || fastFlick) {
        // Commit. Move to 'settling' so the consumer can transition off-screen.
        setState('committed');
        try {
          onCommitRef.current();
        } catch {
          /* swallow — consumer is responsible for its own error handling */
        }
        if (dismissAfterCommit) {
          // Drive the dismiss animation by setting a large offset and
          // entering the 'settling' state which emits the transition style.
          setState('settling');
          setOffset(direction === 'left' ? -DISMISS_OFFSET_PX : DISMISS_OFFSET_PX);
        } else {
          setState('idle');
          setOffset(0);
        }
        return;
      }

      // Spring back.
      setState('settling');
      setOffset(0);
    },
    [disabled, direction, dismissAfterCommit, releaseCapture],
  );

  const onPointerCancel = useCallback(
    (e: ReactPointerEvent<HTMLElement>) => {
      if (disabled) return;
      if (activePointerIdRef.current !== e.pointerId) return;
      releaseCapture(elementRef.current, e.pointerId);
      activePointerIdRef.current = null;
      setState('settling');
      setOffset(0);
    },
    [disabled, releaseCapture],
  );

  // After a settle transition completes the consumer's onTransitionEnd
  // resets the state. Tests sometimes don't fire transitionend, so also
  // schedule a fallback timer when settling toward 0.
  useEffect(() => {
    if (state !== 'settling') return;
    const settleDelay = reducedMotion ? 0 : 240;
    const t = window.setTimeout(() => {
      setState('idle');
      setTotalDelta(0);
    }, settleDelay);
    return () => window.clearTimeout(t);
  }, [state, reducedMotion]);

  // ─── Bound style ──────────────────────────────────────────────────────

  const transition = (() => {
    if (reducedMotion) return 'none';
    if (state === 'settling') {
      return offset === 0 ? SETTLE_TRANSITION : DISMISS_TRANSITION;
    }
    return 'none';
  })();

  const style: CSSProperties = {
    transform: `translate3d(${offset}px, 0, 0)`,
    transition,
    touchAction: 'pan-y',
    willChange: state === 'dragging' ? 'transform' : undefined,
  };

  const refCallback = useCallback<RefCallback<HTMLElement>>((el) => {
    elementRef.current = el;
  }, []);

  // Progress is computed from the magnitude of the offset, not the raw dx,
  // so vertical-aborted gestures naturally collapse to 0.
  const progress = (() => {
    const t = resolvedThresholdRef.current || DEFAULT_THRESHOLD_PX;
    const magnitude = Math.abs(offset);
    if (magnitude <= 0) return 0;
    if (magnitude >= t) return 1;
    return magnitude / t;
  })();

  return {
    bind: {
      ref: refCallback,
      style,
      onPointerDown,
      onPointerMove,
      onPointerUp,
      onPointerCancel,
    },
    state,
    progress,
    totalDelta,
  };
}

// ─── SwipeRevealBackground helper ───────────────────────────────────────────
//
// Renders the colored slab that lives behind a swipeable element. Opacity
// of the label ramps in from 0 to 1 as `progress` crosses 0.6 → 1.0 so the
// affordance only "shouts" when the user is committed.
//
// The `side` prop marks which edge of the card the affordance peeks from:
// - `side='right'`  → card travels left; reveal anchors on the right; gradient
//                     shoots from the right edge (`bg-gradient-to-l`).
// - `side='left'`   → card travels right; reveal anchors on the left; gradient
//                     shoots from the left edge (`bg-gradient-to-r`).

interface SwipeRevealBackgroundProps {
  progress: number;
  state: SwipeState;
  label: string;
  icon: ReactNode;
  /** Which side of the wrapper the affordance peeks from. Default 'right'. */
  side?: 'left' | 'right';
  className?: string;
}

export function SwipeRevealBackground({
  progress,
  state,
  label,
  icon,
  side = 'right',
  className,
}: SwipeRevealBackgroundProps) {
  // Label fades in past 0.6 progress; icon scales up slightly past 1.0.
  const labelOpacity = Math.max(0, Math.min(1, (progress - 0.6) / 0.4));
  const iconScale = progress >= 1 && state !== 'settling' ? 1.15 : 1;
  const visible = state === 'dragging' || state === 'committed' || state === 'settling';

  // Gradient must enter from whichever edge the affordance peeks from so
  // the strongest emerald sits under the icon/label.
  const gradientDirectionClass =
    side === 'right' ? 'bg-gradient-to-l' : 'bg-gradient-to-r';

  return (
    <div
      aria-hidden="true"
      className={cn(
        'pointer-events-none absolute inset-0 rounded-2xl overflow-hidden',
        gradientDirectionClass,
        'from-emerald-600 to-teal-600',
        'dark:from-emerald-700 dark:to-teal-700',
        'shadow-inner',
        visible ? 'opacity-100' : 'opacity-0',
        'transition-opacity duration-150',
        className,
      )}
    >
      <div
        className={cn(
          'absolute inset-y-0 flex items-center gap-3 px-5 text-white',
          'dark:text-emerald-50',
          side === 'right' ? 'right-0' : 'left-0',
        )}
      >
        <span
          className="text-xs font-semibold uppercase tracking-widest tabular-nums"
          style={{ opacity: labelOpacity }}
        >
          {label}
        </span>
        <span
          className="flex items-center justify-center"
          style={{
            transform: `scale(${iconScale})`,
            transition: 'transform 120ms ease-out',
          }}
        >
          {icon}
        </span>
      </div>
    </div>
  );
}
